import { CdkStep, CdkStepper } from '@angular/cdk/stepper';
import { Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { GetGeneralInfoApp } from '@app/core/store/app.actions';
import { AppState } from '@app/core/store/app.state';
import {
    SetCountryCodePhoneNumber,
    SetCountryCodeWhatSapp,
} from '@app/pages/web-site/contact-data/store/contact-data.actions';
import { ContactDataState } from '@app/pages/web-site/contact-data/store/contact-data.state';
import { FormDataStorage } from '@app/_interfaces/contact_data';
import { SocialRed } from '@app/_models/social-media/social-media.model';
import { McToastService } from '@app/_services';
import { SocialMediaHelper } from '@app/_shared/helpers/socialMediaHelper';
import { StepperHelper } from '@app/_shared/helpers/StepperHelper';
import { utils } from '@app/_utils';
import * as CustomValidators from '@app/_validators';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { countryCodes } from '../../../../_shared/data/country-codes';
import {
    InitializeWizard,
    SetIaFinished,
    SetIsLoading,
    SetMarketSectionsIa,
    SetOpenModalStepper,
    SetWizardStatus,
    WizardSetContactData,
    WizardSetWspData,
} from '../../store/stepper.actions';
import { StepperState } from '../../store/stepper.state';
import { CountryLanguage } from '@app/_models/languages/language';
import { PageLanguagesState } from '@app/pages/web-site/settings/page-languages/store/page-languages.state';
import { GetLanguagesCountries } from '@app/pages/web-site/settings/page-languages/store/page-languages.actions';
import { GetInitialInfo } from '@app/core/sidebar/store/sidebar.actions';
import { SidebarState } from '@app/core/sidebar/store/sidebar.state';
import { InitialInfo } from '@app/_interfaces';
import { TranslateService } from '@ngx-translate/core';
import { WizardErrorModalComponent } from '../../stepper-components/wizard-error-modal/wizard-error-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'social-media',
    templateUrl: './social-media.component.html',
    styleUrls: ['./social-media.component.css'],
})
export class SocialMediaComponent implements OnInit, OnDestroy {
    @ViewChildren('inputElement') inputElements: QueryList<ElementRef>;

    contactDataForm: UntypedFormGroup;
    submitted: boolean;
    flagIa: boolean = false;
    countries = countryCodes.sort((a, b) => a.id.localeCompare(b.id));
    selectedValue: string;
    formDataStorage: FormDataStorage;
    location: string = 'Argentina';
    direction: string = `https://maps.google.com/maps?q=${location}&amp;&output=embed`;

    countryCodePhone: CountryLanguage;
    countryCodeWp: CountryLanguage;
    defaultCountryFlag: CountryLanguage;

    @Select(PageLanguagesState.languagesCountries)
    languagesCountries$: Observable<CountryLanguage[]>;

    @Select(SidebarState.initialInfo)
    initialInfo$: Observable<InitialInfo>;

    languagesCountries: CountryLanguage[] = [];

    @Input() stepName: string;
    @Select(ContactDataState.countryCodePhoneNumber)
    countryCodePhoneNumber$: Observable<CountryLanguage>;
    @Select(ContactDataState.countryCodeWp)
    countryCodeWp$: Observable<CountryLanguage>;
    @Select(StepperState.isLoading)
    isLoading$: Observable<boolean>;

    @ViewChildren('addressInputElement') addressInputElement: QueryList<ElementRef>;

    @Select(AppState.isMobile)
    isMobile$: Observable<boolean>;

    constructor(
        private fb: UntypedFormBuilder,
        private store: Store,
        private stepperHelper: StepperHelper,
        private cdkStep: CdkStep,
        private mcToast: McToastService,
        private dialog: NgbModal,
        private cdkStepper: CdkStepper,
        private socialMediaHelper: SocialMediaHelper,
        private translate: TranslateService
    ) {}

    ngOnInit(): void {
        if (this.stepperHelper.findStep('create-site-ia') && this.stepperHelper.getCurrentStepIndex() <= 3)
            this.flagIa = true;
        this.stepperHelper.setCurrentStepName(this.stepName);
        this.getCountries();
        this.stepperHelper.rememberStatus(this.stepName, this.cdkStep);
        this.changeAddress(this.location);
    }

    ngOnDestroy(): void {
        this.phoneNumberToStorage();
        this.stepperHelper.rememberFormData(this.stepName, this.contactDataForm?.value);
        this.stepperHelper.updateStatusStep(this.stepName, this.cdkStep);
    }

    phoneNumberToStorage(): void {
        let whatsappValue;
        let phoneNumberValue;
        if (this.contactDataForm?.value.whatsapp)
            whatsappValue = `+${this.countryCodeWp?.prefix_phone} ${this.contactDataForm?.value.whatsapp}`;
        if (this.contactDataForm?.value.phone)
            phoneNumberValue = `+${this.countryCodePhone?.prefix_phone} ${this.contactDataForm?.value.phone}`;
        this.contactDataForm?.controls['whatsapp'].setValue(whatsappValue);
        this.contactDataForm?.controls['phone'].setValue(phoneNumberValue);
    }

    buildForm(): void {
        this.contactDataForm = this.fb.group({
            addressess: this.fb.array(this.createAddressControl(this.formDataStorage?.formValue?.adressess)),
            phone: [
                this.formDataStorage ? this.formDataStorage.formValue?.phone?.trim().split(' ')[1] : '',
                [CustomValidators.phoneNumber(), Validators.minLength(6)],
            ],
            whatsapp: [
                this.formDataStorage ? this.formDataStorage.formValue?.whatsapp?.trim().split(' ')[1] : '',
                [CustomValidators.wp(), Validators.minLength(6)],
            ],
            email: [
                this.formDataStorage ? this.formDataStorage.formValue?.email : '',
                [Validators.required, Validators.minLength(1), CustomValidators.email()],
            ],
            socialMedia: this.fb.array(
                this.createSocialControl(this.formDataStorage ? this.formDataStorage.formValue?.socialMedia : '')
            ),
        });
    }

    hasDataLoaded(): void {
        this.formDataStorage = this.stepperHelper.getFormData(this.stepName);
        if (this.formDataStorage?.formValue?.phone) {
            const number = this.formDataStorage.formValue.phone;
            const country = utils.getCountryFlag(number);
            this.selectCountryCodePhone(country);
        } else {
            this.selectCountryCodePhone();
        }
        if (this.formDataStorage?.formValue?.whatsapp) {
            const number = this.formDataStorage.formValue.whatsapp;
            const country = utils.getCountryFlag(number);
            this.selectCountryCodeWp(country);
        } else {
            this.selectCountryCodeWp();
        }
    }

    createSocialControl(data): Array<UntypedFormControl> {
        let controls = [];
        if (data && data.length >= 1 && data[0] != null) {
            data.forEach((element) => {
                if (element.url) {
                    controls.push(
                        new UntypedFormControl(element.url, CustomValidators.isSocialMedia(this.socialMediaHelper))
                    );
                } else {
                    controls.push(
                        new UntypedFormControl(element, CustomValidators.isSocialMedia(this.socialMediaHelper))
                    );
                }
            });
        } else {
            controls.push(new UntypedFormControl('', CustomValidators.isSocialMedia(this.socialMediaHelper)));
        }
        return controls;
    }

    get getSocialMedia() {
        return this.contactDataForm.get('socialMedia') as UntypedFormArray;
    }

    addSocialNetwork(): void {
        this.inputElements.changes.subscribe(() => {
            this.inputElements.last.nativeElement.focus();
        });
        let addSuccess: boolean = false;
        this.getSocialMedia.value.forEach((value) => {
            !value ? (addSuccess = false) : (addSuccess = true);
        });
        if (addSuccess && this.getSocialMedia.status == 'VALID') {
            if (this.getSocialMedia.controls.length < 8) {
                this.getSocialMedia.push(
                    this.fb.control('', [CustomValidators.isSocialMedia(this.socialMediaHelper), Validators.required])
                );
            } else {
                this.mcToast.warning('No se puede agregar más de ocho redes sociales');
            }
        }
    }

    deleteSocialNetwork(index): void {
        if (this.getSocialMedia.controls.length > 1) {
            const control = <UntypedFormArray>this.contactDataForm.controls['socialMedia'];
            control.removeAt(index);
            this.setValidators();
        }
    }

    selectCountryCodePhone(event?): void {
        const alpha2CodeBO = this.store.selectSnapshot(AppState.getConfig)?.isoPais;
        const country = this.languagesCountries.find((c) => c.iso_code_2 === alpha2CodeBO);
        let countryCode;
        countryCode = this.defaultCountryFlag;
        if (alpha2CodeBO) countryCode = country;
        if (event) countryCode = event;
        this.countryCodePhone = countryCode;
        this.store.dispatch(new SetCountryCodePhoneNumber(countryCode));
    }

    selectCountryCodeWp(event?): void {
        const alpha2CodeBO = this.store.selectSnapshot(AppState.getConfig)?.isoPais;
        const country = this.languagesCountries.find((c) => c.iso_code_2 === alpha2CodeBO);
        let countryCode;
        countryCode = this.defaultCountryFlag;
        if (alpha2CodeBO) countryCode = country;
        if (event) countryCode = event;
        this.countryCodeWp = countryCode;
        this.store.dispatch(new SetCountryCodeWhatSapp(countryCode));
    }

    dataToSend(formValue) {
        const socialDataValue: SocialRed[] = [];
        let whatsappValue;
        let phoneNumberValue;
        formValue.socialMedia.forEach((element) => {
            const socialRed = this.socialMediaHelper.socialExist(element);
            if (socialRed) {
                socialDataValue.push({ type: socialRed.type, url: element });
            }
        });
        if (formValue.whatsapp) {
            if (typeof this.countryCodeWp?.prefix_phone !== 'undefined') {
                whatsappValue = `+${this.countryCodeWp?.prefix_phone} ${formValue.whatsapp}`;
            } else {
                whatsappValue = `+54 ${formValue.whatsapp}`;
            }
        }
        if (formValue.phone) {
            if (typeof this.countryCodePhone?.prefix_phone !== 'undefined') {
                phoneNumberValue = `+${this.countryCodePhone?.prefix_phone} ${formValue.phone}`;
            } else {
                phoneNumberValue = `+54 ${formValue.phone}`;
            }
        }
        return {
            address: formValue.addressess,
            phone: phoneNumberValue,
            whatsapp: whatsappValue,
            contactEmail: formValue.email,
            showEmail: true,
            socialMedia: socialDataValue,
            botonWhatsapp: {
                mostrar_boton_whatsapp_flotante: whatsappValue !== undefined,
                alinear_izquierda_boton_whatsapp: false,
                whatsapp_compartir_url_producto: false,
                whatsapp_texto_predeterminado: '',
            },
        };
    }

    onFormSubmit(): void {
        const formValue = this.contactDataForm.value;
        this.stepperHelper.sendStepperStatus();
        const contactDataValue = this.dataToSend(formValue);
        this.stepperHelper.updateDataStep(this.stepName, contactDataValue);
        this.submitted = true;
        if (this.contactDataForm.valid) {
            this.store
                .dispatch([
                    new SetIsLoading(true),
                    new WizardSetContactData(contactDataValue),
                    new WizardSetWspData(contactDataValue.botonWhatsapp),
                ])
                .subscribe({
                    next: () => {
                        if (!this.flagIa) this.finishInitialWizard();
                        else {
                            const auxPrompt = localStorage.getItem('userPrompt');
                            const randomTemplate = this.store.selectSnapshot(StepperState.randomTemplate);
                            const auxDataTemplates = {
                                pages: randomTemplate.pages.map((p) => ({ id: p.id })),
                                userPrompt: auxPrompt,
                            };
                            this.cdkStepper.next();
                            this.store.dispatch(new SetMarketSectionsIa(auxDataTemplates)).subscribe({
                                next: () => {
                                    this.store.dispatch(new SetIaFinished(true));
                                    this.store.dispatch(new SetIsLoading(false));
                                },
                                error: (err) => {
                                    const modalRef = this.dialog.open(WizardErrorModalComponent);
                                    let data = { err };
                                    modalRef.componentInstance.fromParent = data;
                                },
                            });
                        }
                    },
                });
        }
    }

    finishInitialWizard(): void {
        this.store.dispatch(new SetWizardStatus(true)).subscribe({
            next: () => {
                this.store.dispatch(new GetGeneralInfoApp());
                setTimeout(() => {
                    this.store.dispatch(new InitializeWizard(false));
                    this.store.dispatch(new SetIsLoading(false));
                    this.store.dispatch(new SetOpenModalStepper(true));
                    this.stepperHelper.deleteStatusWizard();
                }, 5000);
            },
        });
    }

    setValidators(): void {
        if (this.getSocialMedia.controls.length == 1) {
            this.getSocialMedia.controls[0].clearValidators();
            this.getSocialMedia.controls[0].setValidators([CustomValidators.isSocialMedia(this.socialMediaHelper)]);
            this.getSocialMedia.controls[0].updateValueAndValidity();
        }
    }

    changeAddress(value) {
        this.direction = `https://maps.google.com/maps?q=${!value?.length ? this.location : value}&amp;&output=embed`;
    }

    getCountries() {
        this.store.dispatch(new GetLanguagesCountries()).subscribe(() => {
            this.languagesCountries = this.store.selectSnapshot(PageLanguagesState.languagesCountries);
            this.defaultCountryFlag = this.languagesCountries?.find((c) => c.iso_code_2 === 'AR');
            this.hasDataLoaded();
            setTimeout(() => {
                this.buildForm();
                // this.store.dispatch(new GetInitialInfo()).subscribe({
                //     next: () => {
                //         this.initialInfo$.subscribe((value) => {
                //             if (value.data.mainMarket.email) {
                //                 this.contactDataForm?.controls['email'].setValue(value.data.mainMarket.email);
                //             }
                //         });
                //     },
                // });
                this.initialInfo$.subscribe((value) => {
                    if (value.data.mainMarket.email) {
                        this.contactDataForm?.controls['email'].setValue(value.data.mainMarket.email);
                    }
                });
            }, 500);
        });
    }

    // Domicilios
    createAddressControl(data: string[]) {
        let controls = [];
        if (data && data.length >= 1 && data[0] != null) {
            this.changeAddress(data[0]);
            data.forEach((element) => {
                controls.push(new UntypedFormControl(element, [Validators.maxLength(200)]));
            });
        } else {
            this.changeAddress(this.location);
            controls.push(new UntypedFormControl('', [Validators.maxLength(200)]));
        }
        return controls;
    }

    get getaddressess() {
        return this.contactDataForm?.get('addressess') as UntypedFormArray;
    }

    addAddress() {
        this.addressInputElement.changes.subscribe(() => {
            this.addressInputElement.last.nativeElement.focus();
        });
        let addSuccess: boolean = false;
        this.getaddressess.value.forEach((value) => {
            !value ? (addSuccess = false) : (addSuccess = true);
        });
        if (addSuccess && this.getaddressess?.status == 'VALID') {
            if (this.getaddressess?.controls?.length < 5) {
                this.getaddressess?.push(this.fb.control('', []));
            } else {
                this.mcToast.warning(this.translate.instant('app.configuracion.toast.maximo_direcciones'));
            }
        }
    }

    deleteAddress(index) {
        if (this.getaddressess?.controls?.length > 1) {
            const control = <UntypedFormArray>this.contactDataForm.controls['addressess'];
            control.removeAt(index);
            this.setValidators();
        }
    }

    setAddressPin(index): void {
        if (this.getaddressess?.controls?.length > 1) {
            const addressessArray = this.contactDataForm?.get('addressess') as UntypedFormArray;
            const control = addressessArray.at(index);
            if (control?.value?.length) {
                this.changeAddress(control.value);
            }
        }
    }

    get allAddressesFilled(): boolean {
        const addAddressControls = this.getaddressess;
        return addAddressControls?.controls.every((control) => control.value?.trim().length > 0);
    }
}
